/**
 * Shared Formatting Utilities
 * Consolidated formatting functions used across frontend and backend
 */

import type { LiveGameScores } from './hooks/useLiveStats';
import { easternDay, formatCalendarDay } from './time';

/**
 * Formats a date string as a relative time (e.g., "5m ago", "2h ago", "3d ago")
 */
export function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Formats a tracked parlay's creation date in the device's zone.
 * Shows relative time if < 24h, otherwise shows full date with time.
 * Examples: "Just now", "5m ago", "2h ago", "Jan 14, 3:00 PM"
 */
export function formatTrackedDate(dateStr: string): string {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 1) {
    const mins = Math.floor(diffMs / (1000 * 60));
    return mins <= 1 ? 'Just now' : `${mins}m ago`;
  }
  if (diffHours < 24) {
    return `${Math.floor(diffHours)}h ago`;
  }

  return (
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ', ' +
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  );
}

/**
 * Formats a game's date with no time, e.g. "Jan 14". The tip instant shows in the device's zone;
 * without one, the stored game day shows as its calendar day on every device.
 */
export function formatGameDate(gameStartsAt: string | null, gameDate: string | null): string {
  const short: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (gameStartsAt) {
    const tip = new Date(gameStartsAt);
    return Number.isNaN(tip.getTime()) ? '' : tip.toLocaleDateString('en-US', short);
  }
  if (!gameDate) return '';
  try {
    return formatCalendarDay(easternDay(gameDate), short);
  } catch {
    return '';
  }
}

/**
 * A live game's clock, e.g. "Q3 5:12" or "OT1 2:00"; '' in every other state.
 * A finished game gets no text because its FINAL badge already says so.
 */
export function formatGameClock(
  game: Pick<LiveGameScores, 'gameStatus' | 'quarter' | 'timeRemaining'> | null,
): string {
  if (game?.gameStatus !== 'live' || !game.quarter) return '';
  const period = game.quarter <= 4 ? `Q${game.quarter}` : `OT${game.quarter - 4}`;
  return game.timeRemaining ? `${period} ${game.timeRemaining}` : period;
}

/** "Mon, Jan 5" for a calendar day (`YYYY-MM-DD`), the same on every device; '' if it is not one. */
export function formatShortDay(day: string): string {
  try {
    return formatCalendarDay(day, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/** "Today", "Tomorrow" or "Mon, Jan 5" for the day an instant falls on in the device's zone. */
export function formatRelativeDay(isoString: string): string {
  const date = new Date(isoString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Formats ROI as a signed percentage string (e.g., "+5.2%", "-3.1%")
 */
export function formatROI(roi: number): string {
  const sign = roi >= 0 ? '+' : '';
  return `${sign}${roi.toFixed(1)}%`;
}

/**
 * Formats a number as a percentage string.
 * By default, assumes the value is a decimal (0.75 -> "75.0%").
 * Pass `alreadyDecimal: true` if the value is already a percentage (75.0 -> "75.0%").
 *
 * Note: The `alreadyDecimal` option name refers to whether the value is already
 * in percentage form (not needing multiplication by 100).
 */
export function formatPercent(value: number, options?: { alreadyDecimal?: boolean }): string {
  const percentage = options?.alreadyDecimal ? value : value * 100;
  return `${percentage.toFixed(1)}%`;
}

/**
 * Formats a win/loss streak (e.g., 3 -> "3W", -2 -> "2L", 0 -> "-")
 */
export function formatStreak(streak: number): string {
  if (streak === 0) return '-';
  const abs = Math.abs(streak);
  const letter = streak > 0 ? 'W' : 'L';
  return `${abs}${letter}`;
}

/**
 * Formats an amount in cents as a dollar string (e.g., 12345 -> "$123.45")
 */
export function formatCents(cents: number): string {
  const dollars = Math.abs(cents) / 100;
  const formatted = dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return cents < 0 ? `-$${formatted}` : `$${formatted}`;
}

/**
 * Formats a period label ("2026-02") as a human-readable month ("February 2026")
 */
export function formatPeriodLabel(periodLabel: string): string {
  const [year, month] = periodLabel.split('-');
  return new Date(Number(year), Number(month) - 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}
